import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Badge,
  Spinner,
  Form,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import axios from "./axiosConfig";
import CreateProbeModal from "./CreateProbeModal";

type ProbeNode = {
  id: number;
  name: string;
  probe_code: string;
  base_url: string;
  status: "UP" | "DOWN" | "UNKNOWN";
  enabled: boolean;
  tags?: any;
  updated_at?: string;
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const variant =
    status === "UP"
      ? "success"
      : status === "DOWN"
      ? "danger"
      : "secondary";

  return <Badge bg={variant}>{status}</Badge>;
};

const ProbeListPage: React.FC = () => {
  const [probes, setProbes] = useState<ProbeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const fetchProbes = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/probes");
      setProbes(res.data || []);
    } catch (e) {
      console.error("Failed to fetch probes", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProbes();
  }, []);

  const toggleEnabled = async (probe: ProbeNode) => {
    try {
      await axios.patch(`/api/probes/${probe.id}/enabled`, {
        enabled: !probe.enabled,
      });
      setProbes((prev) =>
        prev.map((p) =>
          p.id === probe.id ? { ...p, enabled: !p.enabled } : p
        )
      );
    } catch (e) {
      console.error("Failed to toggle probe", e);
    }
  };

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <h3>Probes</h3>
        </Col>
        <Col className="text-end">
          <Button onClick={() => setShowCreate(true)}>
            Create Probe
          </Button>
        </Col>
      </Row>

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Table bordered hover size="sm">
          <thead>
            <tr>
              <th>Status</th>
              <th>Name</th>
              <th>Probe Code</th>
              <th>Base URL</th>
              <th>Enabled</th>
              <th>Tags</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {probes.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-muted">
                  No probes configured
                </td>
              </tr>
            )}

            {probes.map((probe) => (
              <tr key={probe.id}>
                <td>
                  <StatusBadge status={probe.status} />
                </td>
                <td>{probe.name}</td>
                <td>
                  <code>{probe.probe_code}</code>
                </td>
                <td>
                  <a
                    href={probe.base_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {probe.base_url}
                  </a>
                </td>
                <td>
                  <Form.Check
                    type="switch"
                    checked={probe.enabled}
                    onChange={() => toggleEnabled(probe)}
                  />
                </td>
                <td>
                  {Array.isArray(probe.tags) &&
                    probe.tags.map((t: string) => (
                      <Badge
                        key={t}
                        bg="secondary"
                        className="me-1"
                      >
                        {t}
                      </Badge>
                    ))}
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() =>
                      alert("Edit page to be implemented")
                    }
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <CreateProbeModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={fetchProbes}
      />
    </Container>
  );
};

export default ProbeListPage;
